// src/utils/analytics.js
//
// Wrapper tipis di atas gtag.js (GA4). Semua fungsi otomatis menjadi no-op
// kalau VUE_APP_GA_ID kosong atau gtag belum ter-load, jadi aman dipanggil
// dari komponen mana pun tanpa guard tambahan.
//
// JANGAN kirim data pribadi (PII) ke GA4: tanpa nama, nomor HP, email, NIK,
// atau no_mr. user_code bersifat pseudonim sehingga boleh dipakai sebagai user_id.

const GA_ID = process.env.VUE_APP_GA_ID;

/** True hanya bila GA4 aktif dan gtag tersedia. */
function gaReady() {
  return Boolean(GA_ID) && typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Bersihkan params: buang null/undefined, potong string panjang (batas GA4),
 * agar tidak ada nilai aneh yang terkirim.
 */
function sanitize(params = {}) {
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string") {
      // Buang string kosong / spasi-saja: biar GA4 mencatat "(not set)" alih-alih
      // baris kosong yang mengotori laporan (mis. dimensi Judul Artikel).
      const trimmed = value.trim();
      if (trimmed === "") continue;
      out[key] = trimmed.slice(0, 100);
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Alamat halaman yang boleh dikirim ke GA4.
 *
 * Rute berparameter dikirim sebagai POLANYA (mis. "/riwayat-luka/:mr/:dob"), bukan
 * nilainya: di aplikasi ini parameter rute hampir selalu menunjuk catatan milik seorang
 * pasien — No. MR + tanggal lahir, ID pasien, kode pesanan, token kuitansi. Rute konten
 * publik yang laporannya butuh nilai asli (slug artikel dibaca ga4:sync-article-views
 * di rks) ditandai `meta.gaAlamatAsli` di router.
 * @param {object} route  rute vue-router (dari afterEach atau router.resolve)
 */
export function alamatHalaman(route) {
  const terisi = Object.values(route.params || {}).some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));
  const rekaman = route.matched && route.matched[route.matched.length - 1];
  if (terisi && rekaman && !route.meta.gaAlamatAsli) return rekaman.path.replace(/\?/g, "");
  return route.fullPath;
}

/**
 * True selama setelan GA4 "Page changes based on browser history events" (Admin → Data
 * streams → Enhanced measurement) masih aktif. gtag.js memasang pendengarnya dengan menambal
 * history.pushState, lalu mengirim page_view sendiri tiap URL berubah — dengan URL ASLI dari
 * address bar, yang tidak bisa disamarkan dari sini. Setelan itu harus dimatikan.
 */
function gaMencatatPindahHalaman() {
  return Object.prototype.hasOwnProperty.call(window.history, "pushState");
}

/**
 * Catat halaman yang baru dibuka dengan alamat dari alamatHalaman().
 * @param {object} route  rute tujuan dari router.afterEach
 * @param {object} dari   rute asal (START_LOCATION saat halaman pertama dimuat)
 */
export function catatHalaman(route, dari) {
  if (!gaReady()) return;
  const lokasi = window.location.origin + alamatHalaman(route);
  const halamanPertama = !dari || !dari.matched.length;
  try {
    // Lewat "set", bukan config: dipakai event otomatis sesudahnya (scroll, user_engagement)
    // dan trackEvent(). page_location di config justru mengunci alamat halaman pertama.
    window.gtag("set", { page_location: lokasi, page_title: document.title });

    // Selama pendengar riwayat GA masih aktif, ia sudah mengirim page_view untuk setiap URL
    // yang berubah; page_view kita hanya akan menggandakannya. Yang tak terlihat olehnya cuma
    // halaman pertama yang URL-nya tidak berubah.
    if (gaMencatatPindahHalaman() && (!halamanPertama || route.redirectedFrom)) return;

    const params = { page_location: lokasi, page_title: document.title };
    // Halaman pertama memakai perujuk yang sudah disamarkan lewat "set" di main.js.
    if (!halamanPertama) params.page_referrer = window.location.origin + alamatHalaman(dari);
    window.gtag("event", "page_view", params);
  } catch (e) {
    console.warn("[analytics] catatHalaman gagal:", e);
  }
}

/**
 * Kirim custom event ke GA4.
 * @param {string} name  nama event snake_case, < 40 char (mis. "cek_gula_darah")
 * @param {object} [params] parameter event (snake_case, tanpa PII)
 */
export function trackEvent(name, params = {}) {
  if (!gaReady() || !name) return;
  try {
    window.gtag("event", name, sanitize(params));
  } catch (e) {
    // jangan pernah mengganggu UX karena analytics gagal
    console.warn("[analytics] trackEvent gagal:", e);
  }
}

/**
 * Set user_id (untuk akurasi DAU/MAU lintas device). Pakai user_code yang
 * pseudonim — JANGAN gunakan nomor HP / nama.
 * @param {string|number} userId
 */
export function setAnalyticsUser(userId) {
  if (!gaReady() || !userId) return;
  try {
    window.gtag("config", GA_ID, { user_id: String(userId), send_page_view: false });
  } catch (e) {
    console.warn("[analytics] setAnalyticsUser gagal:", e);
  }
}

/**
 * Hapus user_id saat logout (mis. kalau nanti ada tombol logout eksplisit).
 */
export function clearAnalyticsUser() {
  if (!gaReady()) return;
  try {
    window.gtag("config", GA_ID, { user_id: null, send_page_view: false });
  } catch (e) {
    console.warn("[analytics] clearAnalyticsUser gagal:", e);
  }
}
